import { useEffect, useRef, useState, type FormEvent } from 'react';
import type {
  CorrectionChange,
  CorrectionPreview,
  DiaryEntry,
  EntryItem,
  ItemSources,
  Nutrients,
} from '@calorie/shared';
import { haptic, hapticResult } from '../lib/telegram.js';
import { formatQuantity, nutritionAmount } from '@calorie/shared';

interface Props {
  entry: DiaryEntry;
  onPreview: (text: string) => Promise<{ understood: boolean; preview: CorrectionPreview | null }>;
  onApply: (changes: CorrectionChange[]) => Promise<void>;
  onClose: () => void;
}

/** КБЖУ на 100 г позиции: из снимка, иначе обратным пересчётом из съеденного. */
function per100gOf(item: EntryItem): Pick<Nutrients, 'kcal' | 'protein' | 'fat' | 'carbs'> {
  if (item.per100g) return item.per100g;
  const round = (v: number) => Math.round(v * 10) / 10;
  const back = (v: number) => (v / nutritionAmount(item)) * 100;
  return {
    kcal: Math.round(back(item.kcal)),
    protein: round(back(item.protein)),
    fat: round(back(item.fat)),
    carbs: round(back(item.carbs)),
  };
}

/**
 * Текстом: что дали источники по позиции.
 *
 * Источников три - Наша база, USDA и FatSecret, - но у части записей внешних
 * нет: их не было на момент записи либо продукт по-английски не назвался.
 * Перечисляем только те, что реально ответили.
 */
function sourceText(s: ItemSources | undefined, basis = 'г'): string {
  if (!s) return 'Источник: Наша база (сверка недоступна)';

  const parts: string[] = [];
  if (s.ours) parts.push(`Наша база ${s.ours.kcal}`);
  if (s.usda) parts.push(`USDA ${s.usda.kcal}`);
  if (s.fatsecret) parts.push(`FatSecret ${s.fatsecret.kcal}`);

  if (parts.length === 0) return 'Источник неизвестен';
  if (parts.length === 1) return `Источник: ${parts[0]} ккал/100 ${basis}`;

  const verdict = s.averaged ? 'совпали, взято среднее' : 'разошлись, взято наше';
  return `${parts.join(' · ')} ккал - ${verdict}`;
}

/** Человеческое описание того, что ИИ понял из правки. */
function describeChanges(changes: CorrectionChange[], items: EntryItem[]): string {
  const parts: string[] = [];
  for (const ch of changes) {
    const name = items[ch.item]?.name ?? 'позиция';
    const bits: string[] = [];
    if (ch.grams != null) bits.push(`${ch.grams} г`);
    if (ch.volumeMl != null) bits.push(`${ch.volumeMl} мл`);
    const basis = (ch.nutritionBasis ?? items[ch.item]?.nutritionBasis) === 'ml' ? 'мл' : 'г';
    if (ch.kcal100 != null) bits.push(`${ch.kcal100} ккал/100 ${basis}`);
    if (ch.protein100 != null) bits.push(`Б ${ch.protein100}/100 ${basis}`);
    if (ch.fat100 != null) bits.push(`Ж ${ch.fat100}/100 ${basis}`);
    if (ch.carbs100 != null) bits.push(`У ${ch.carbs100}/100 ${basis}`);
    if (bits.length) parts.push(`«${name}» → ${bits.join(', ')}`);
  }
  return parts.join('; ');
}

type Mode = 'view' | 'ask' | 'confirm';

export function EntryDetails({ entry, onPreview, onApply, onClose }: Props) {
  const [mode, setMode] = useState<Mode>('view');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [pending, setPending] = useState<CorrectionPreview | null>(null);
  const [closing, setClosing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const filled = text.trim().length > 1;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function close() {
    if (closing) return;
    setClosing(true);
    setTimeout(onClose, 200);
  }

  function openAsk() {
    haptic('light');
    setMode('ask');
    setNote(null);
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const value = text.trim();
    if (value.length < 2 || busy) return;
    haptic('medium');
    setBusy(true);
    setNote(null);
    try {
      const res = await onPreview(value);
      if (res.understood && res.preview) {
        setPending(res.preview);
        setMode('confirm');
      } else {
        hapticResult('warning');
        setNote('Не понял правку. Попробуйте точнее - «съел 250 г» или «180 ккал на 100 г».');
      }
    } catch (error) {
      hapticResult('error');
      setNote(error instanceof Error ? error.message : 'Не получилось разобрать. Проверьте соединение.');
    } finally {
      setBusy(false);
    }
  }

  async function apply() {
    if (!pending || busy) return;
    setBusy(true);
    try {
      await onApply(pending.changes);
      hapticResult('success');
      close();
    } catch (error) {
      hapticResult('error');
      setNote(error instanceof Error ? error.message : 'Не получилось применить.');
      setBusy(false);
    }
  }

  const preview = pending?.entry;

  return (
    <div
      className={`sheet-backdrop${closing ? ' closing' : ''}`}
      onClick={close}
      role="presentation"
    >
      <div
        className={`sheet glass${closing ? ' closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Из чего сложились калории"
      >
        <div className="sheet-grabber" />
        <div className="sheet-head">
          <div className="sheet-titles">
            <span className="sheet-title">{entry.text}</span>
            {/* Название в ленте опрятное, но человек должен видеть, что
                написал сам: по этой фразе он и вспоминает запись */}
            {entry.sourceText && entry.sourceText !== entry.text && (
              <span className="sheet-source">«{entry.sourceText}»</span>
            )}
          </div>
          <button className="icon-btn" onClick={close} aria-label="Закрыть">
            ✕
          </button>
        </div>

        <div className="sheet-body">
          <div className="det-total">
            {Math.round(entry.kcal)} ккал · Б {entry.protein} · Ж {entry.fat} · У {entry.carbs}
          </div>

          {entry.items.length === 0 ? (
            <p className="sheet-empty">Разбор по продуктам недоступен для этой записи.</p>
          ) : (
            <ul className="det-list">
              {entry.items.map((item, i) => {
                const p = per100gOf(item);
                return (
                  <li className="det-item" key={`${item.name}-${i}`}>
                    <div className="det-item-head">
                      <span className="det-name">{item.name}</span>
                      <span className="det-grams">{formatQuantity(item)}</span>
                    </div>
                    <div className="det-per100">
                      На 100 {item.nutritionBasis === 'ml' ? 'мл' : 'г'}: {p.kcal} ккал · Б {p.protein} · Ж {p.fat} · У {p.carbs}
                    </div>
                    <div className="det-src">{sourceText(item.sources, item.nutritionBasis === 'ml' ? 'мл' : 'г')}</div>
                    {item.volumeMl != null && item.grams != null && item.densityEstimated && (
                      <div className="det-src">≈ {item.grams} г · перевод по справочной плотности</div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {/* ─── Правка ─── */}
          {mode === 'view' && entry.items.length > 0 && (
            <button className="det-error-btn" onClick={openAsk}>
              Есть ошибка?
            </button>
          )}

          {mode === 'ask' && (
            <form className={`corr${filled ? ' filled' : ''}`} onSubmit={submit}>
              <div className="corr-field glass">
                <input
                  ref={inputRef}
                  className="corr-input"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Что не так? «съел 250 г, а не 200»"
                  enterKeyHint="send"
                  autoComplete="off"
                  disabled={busy}
                  aria-label="Опишите ошибку"
                />
              </div>
              <button
                className="corr-send"
                type="submit"
                disabled={!filled || busy}
                aria-hidden={!filled}
                aria-label="Отправить"
              >
                {busy ? (
                  <span className="composer-spinner" />
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M4.3 11.4 19.1 5.2c.9-.4 1.8.5 1.4 1.4l-6.2 14.8c-.4.9-1.7.9-2-.1l-1.8-5.3a1.1 1.1 0 0 0-.7-.7l-5.3-1.8c-1-.3-1-1.6-.2-2Z"
                      fill="currentColor"
                    />
                  </svg>
                )}
              </button>
            </form>
          )}

          {mode === 'confirm' && pending && preview && (
            <div className="corr-confirm">
              <div className="corr-understood">
                Понял: {describeChanges(pending.changes, entry.items)}
              </div>
              <div className="corr-preview">
                Станет: {Math.round(preview.kcal)} ккал · Б {preview.protein} · Ж{' '}
                {preview.fat} · У {preview.carbs}
              </div>
              <div className="corr-actions">
                <button
                  className="btn btn-ghost"
                  onClick={() => {
                    setMode('ask');
                    setPending(null);
                  }}
                  disabled={busy}
                >
                  Отмена
                </button>
                <button className="btn" onClick={apply} disabled={busy}>
                  {busy ? <span className="composer-spinner" /> : 'Применить'}
                </button>
              </div>
            </div>
          )}

          {note && <p className="meal-note">{note}</p>}
        </div>
      </div>
    </div>
  );
}
